import { Button } from "@svea/design-system-components-react";
import {
	type AnyFunction,
	ButtonGroup,
	Dialog,
	formatPhone,
	isRequired,
	P,
	type Phone,
	ReactSelectField,
	Stack,
	Text,
	TextField,
	useText,
	validateEmail,
	validatePhoneNumber,
	validateUniqueString,
} from "@sveabank/cc-common";
import { useRef } from "react";
import { Form } from "react-final-form";
import Paper from "../../../../components/paper";
import { usePageContext } from "../../../../hooks/context";
import type { ClcPerson, ClcShareholder } from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";

type Props = {
	open: boolean;
	setOpen: (isOpen: boolean) => void;
	person: ClcShareholder;
	saveShareholder: (shareholder: ClcShareholder) => void;
	allShareholders: Array<ClcShareholder>;
};

type ShareholderForm = {
	email: string;
	phone: Phone;
};

export const EditShareholderModal = ({
	open,
	setOpen,
	person,
	saveShareholder,
	allShareholders,
}: Props) => {
	const [t, texts] = useText();

	// needed for external form submit:
	const submitShareholderRef = useRef<AnyFunction | null>(null);

	const { countryCodes } = usePageContext(useClcCompanyApplication);

	const onSubmitShareholder = (person: ClcPerson) => (values: ShareholderForm) => {
		const shareholder: ClcShareholder = {
			...person,
			email: values.email,
			numberOfShares: 0,
			phone: {
				countryCode: values.phone.countryCode,
				number: formatPhone(values.phone.number),
			},
		};
		saveShareholder(shareholder);
		setOpen(false);
	};

	const existingPhoneNumbers = allShareholders.map((s) => s.phone.number);
	const existingEmails = allShareholders.map((s) => s.email || "");

	return (
		<Dialog
			isOpen={open}
			setOpen={setOpen}
			center
			size="small"
			heading={t(texts.newCompany.shares.editShareholderModalHeading)}
		>
			<Dialog.Content>
				<Stack gap={4}>
					<Paper className="bg-background-tertiary">
						<Text align="left">
							<Text component="p" bold>
								{person.firstName} {person.lastName}
							</Text>
							<P>{person.postalAddress}</P>
							<P>
								{person.postCode} {person.town}
							</P>
						</Text>
					</Paper>
					<Form<ShareholderForm>
						onSubmit={onSubmitShareholder(person)}
						render={({ handleSubmit }) => {
							// needed for external form submit:
							submitShareholderRef.current = handleSubmit;
							return (
								<form onSubmit={handleSubmit}>
									<Stack gap={2}>
										<Stack direction="row" align="start" gap={2}>
											<ReactSelectField
												label={t(texts.contactInfo.companyContact.countryCode)}
												floatingLabel
												name="phone.countryCode"
												options={countryCodes}
												placeholder={t(texts.common.choose)}
												initialValue={person.phone.countryCode || "+46"}
												validators={[isRequired]}
												menuPortalTarget={null}
											/>

											<TextField
												fullWidth
												label={t(texts.newCompany.companyInfo.phone)}
												name="phone.number"
												initialValue={person.phone.number || undefined}
												validators={[
													isRequired,
													validatePhoneNumber,
													validateUniqueString(
														existingPhoneNumbers,
														texts.validation.phoneAlreadyAdded,
													),
												]}
											/>
										</Stack>
										<TextField
											fullWidth
											label={t(texts.newCompany.companyInfo.email)}
											name="email"
											initialValue={person.email || undefined}
											validators={[
												isRequired,
												validateEmail,
												validateUniqueString(existingEmails, texts.validation.emailAlreadyAdded),
											]}
										/>
									</Stack>
								</form>
							);
						}}
					/>
				</Stack>
			</Dialog.Content>
			<Dialog.Footer>
				<ButtonGroup>
					<Button
						onPress={() => {
							submitShareholderRef.current?.();
						}}
					>
						{t(texts.common.save)}
					</Button>

					<Button variant="secondary" onPress={() => setOpen(false)}>
						{t(texts.common.abort)}
					</Button>
				</ButtonGroup>
			</Dialog.Footer>
		</Dialog>
	);
};
